import React, { useCallback, useMemo } from 'react'
import { Box, Flex, Spacer } from '@chakra-ui/react'

import AddLinkButton from './links'
import MethodsSelect from './methods-select'
import CopyTemplateButton from './templates'
import InputTextarea from './input-textarea'
import InternalNoteTextarea from './internal-note-textarea'
import { LocalStorageKeys, SetStateFn } from '~config/constants'
import { useLocalStorage } from '~utils/hooks/use-local-storage'
import AttachmentsDisplay from './attachments/display'
import SuggestChatResponseButton from './ai-suggestion'
import AddAttachmentButton from './attachments/add-button'
import {
    getImageFilesFromClipboard,
    MAX_CHAT_ATTACHMENTS,
    useAddAttachments,
} from './attachments/use-add-attachments'
import SharedStack from '~components/shared/wrappers/shared-stack'
import {
    Attachment,
    ChatFieldsFragment,
    ClientPhoneFieldsFragment,
    MessageMethod,
    AppointmentFieldsFragment,
} from '~graphql/generated/graphql'
import SendButton from './send'
import MessageWarningBanner from './message-warning-banner'
import { getClientMessageWarnings } from './helpers'
import { PatientIdAndName } from './templates/types'
import SmsSegmentCounter from '~components/shared/sms-segment-counter'
import {
    getClinicSmsSegmentInfo,
    shouldExcludeSmsMessageWrapper,
    shouldIgnoreSmsCharacterLimit,
    SMS_SEGMENT_LIMITS,
} from '~utils/sms-segments'
import { useClinic } from '~contexts/clinic-context'

interface Props {
    chat: ChatFieldsFragment
    text: string
    setText: SetStateFn<string>
    loading: boolean
    handleSendMessage: () => void
    messageMethods: MessageMethod[]
    setMessageMethods: SetStateFn<MessageMethod[]>
    resetMessageMethods: () => void
    availablePhones: ClientPhoneFieldsFragment[]
    clientPhone: ClientPhoneFieldsFragment | undefined
    setClientPhone: SetStateFn<ClientPhoneFieldsFragment | undefined>
    baseClientPhone: string | null | undefined

    patientFilter?: string[]
    isInternal?: boolean
    setIsInternal?: SetStateFn<boolean>
    smsDisabledMessage?: string | null
    emailDisabledMessage?: string | null
    attachments?: Attachment[]
    setAttachments?: SetStateFn<Attachment[]>
    shouldCloseChatOnSend?: boolean
    setShouldCloseChatOnSend?: SetStateFn<boolean>
    shouldOpenChatOnSend?: boolean
    setShouldOpenChatOnSend?: SetStateFn<boolean>
    appointment?: AppointmentFieldsFragment
    onCopyTemplatePatient?: (patient: PatientIdAndName | undefined) => void
    onScheduleSend?: (sendTime: number) => void
    isScheduling?: boolean
    /**
     * Compact layout for embedded contexts like the Whiteboard chat popover.
     * Hides secondary action buttons (attachments, templates, links, AI suggestions).
     */
    compact?: boolean
}

const NOOP_SET_ATTACHMENTS: SetStateFn<Attachment[]> = () => undefined

export default function ChatInputComponent({
    chat,
    isInternal,
    setIsInternal,
    smsDisabledMessage,
    emailDisabledMessage,
    messageMethods,
    setMessageMethods,
    attachments,
    setAttachments,
    text,
    setText,
    handleSendMessage,
    loading,
    patientFilter,
    shouldCloseChatOnSend,
    setShouldCloseChatOnSend,
    shouldOpenChatOnSend,
    setShouldOpenChatOnSend,
    resetMessageMethods,
    availablePhones,
    clientPhone,
    setClientPhone,
    baseClientPhone,
    appointment,
    onCopyTemplatePatient,
    onScheduleSend,
    isScheduling,
    compact = false,
}: Props) {
    const { clinic } = useClinic()
    const { addFiles } = useAddAttachments({
        chatId: chat.id,
        setAttachments: setAttachments ?? NOOP_SET_ATTACHMENTS,
        maxAttachments: MAX_CHAT_ATTACHMENTS,
    })

    // Add any images on the clipboard as attachments when pasted into the input.
    // Falls through to default paste behaviour when the clipboard has no images.
    const handlePasteAttachments = useCallback(
        async (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
            if (!attachments || !setAttachments) return

            const imageFiles = await getImageFilesFromClipboard(
                e.clipboardData.items,
            )
            if (!imageFiles.length) return

            e.preventDefault()
            await addFiles(imageFiles)
        },
        [attachments, setAttachments, addFiles],
    )

    // Calculate warnings for the current client and selected message methods
    const messageWarnings = useMemo(
        () =>
            isInternal
                ? []
                : getClientMessageWarnings(
                      chat.client,
                      chat.clinic,
                      messageMethods,
                      clientPhone,
                  ),
        [chat.client, chat.clinic, messageMethods, clientPhone, isInternal],
    )

    // SMS length guardrail for manually typed inbox messages. Only relevant when
    // the message is actually going out over SMS (not internal notes, and not a
    // push/email-only send). Counted against the clinic-wrapped text so it
    // matches what the client receives; personalization tags are counted as
    // typed (their expansion is backstopped by the backend hard ceiling).
    const smsSegmentInfo = useMemo(() => {
        if (isInternal || !messageMethods.includes(MessageMethod.Sms)) {
            return undefined
        }

        return getClinicSmsSegmentInfo({
            body: text,
            clinicName: clinic?.name,
            excludeMessageWrapper: shouldExcludeSmsMessageWrapper(
                'inbox',
                clinic?.settings?.exclude_message_wrapper,
            ),
        })
    }, [isInternal, text, messageMethods, clinic])

    const smsExceedsLimit =
        !!smsSegmentInfo &&
        !shouldIgnoreSmsCharacterLimit(clinic?.tmp_feature_flags) &&
        smsSegmentInfo.segments > SMS_SEGMENT_LIMITS.inboxMessage

    const isSendDisabled =
        (!text && (attachments ? attachments.length === 0 : false)) ||
        smsExceedsLimit

    const [allowCtrlEnterToSend, setAllowCtrlEnterToSend] = useLocalStorage(
        LocalStorageKeys.allowCtrlEnterToSend,
        true,
    )

    // Cmd/Ctrl+Enter sends when the send-options preference is on; plain
    // Enter still inserts a newline.
    const handleComposerKeyDown = useCallback(
        (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
            if (
                !allowCtrlEnterToSend ||
                e.key !== 'Enter' ||
                !(e.ctrlKey || e.metaKey) ||
                e.nativeEvent.isComposing ||
                e.repeat
            ) {
                return
            }

            e.preventDefault()
            if (!loading && !isSendDisabled) {
                handleSendMessage()
            }
        },
        [allowCtrlEnterToSend, handleSendMessage, isSendDisabled, loading],
    )

    return (
        <Flex
            flexDir="column"
            borderWidth={1}
            borderColor={isInternal ? 'orange.100' : 'gray.200'}
            rounded="2xl"
            bg={isInternal ? 'orange.50' : 'white'}
            boxSizing="border-box"
            shadow="blue.lg"
            flex={1}
        >
            <Box px={2} pt={2}>
                <MethodsSelect
                    chat={chat}
                    messageMethods={messageMethods}
                    setMessageMethods={setMessageMethods}
                    resetMessageMethods={resetMessageMethods}
                    smsDisabledMessage={smsDisabledMessage}
                    emailDisabledMessage={emailDisabledMessage}
                    isInternal={isInternal}
                    setIsInternal={setIsInternal}
                    availablePhones={availablePhones}
                    clientPhone={clientPhone}
                    setClientPhone={setClientPhone}
                    baseClientPhone={baseClientPhone}
                />
            </Box>
            <MessageWarningBanner warnings={messageWarnings} />
            {!!smsSegmentInfo && (
                <Box px={3} pt={1}>
                    <SmsSegmentCounter
                        segmentInfo={smsSegmentInfo}
                        limit={SMS_SEGMENT_LIMITS.inboxMessage}
                        sourceText={text}
                        onFixSpecialCharacters={setText}
                        hideUntilNearLimit
                    />
                </Box>
            )}
            {isInternal && chat.clinic?.id ? (
                <InternalNoteTextarea
                    value={text}
                    onChange={setText}
                    placeholder="Add a note..."
                    clinicId={chat.clinic.id}
                    onKeyDown={handleComposerKeyDown}
                    spellCheck
                />
            ) : (
                <InputTextarea
                    value={text}
                    onChange={e => setText(e.target.value)}
                    onPaste={
                        attachments && setAttachments
                            ? handlePasteAttachments
                            : undefined
                    }
                    onKeyDown={handleComposerKeyDown}
                    placeholder={
                        isInternal ? 'Add a note...' : `Send a message...`
                    }
                    spellCheck
                />
            )}
            {!!attachments && setAttachments && (
                <AttachmentsDisplay
                    attachments={attachments}
                    onRemoveAttachment={(index: number) =>
                        setAttachments(prev =>
                            prev.filter((_, i) => i !== index),
                        )
                    }
                />
            )}
            <Flex
                mt={compact ? 1 : 2}
                flexDir="row"
                alignItems="flex-end"
                gap={3}
                px={compact ? 2 : 3}
                pb={compact ? 2 : 3}
                flexWrap="wrap"
            >
                {!isInternal && (
                    <SharedStack row fit flexWrap="wrap" gap={2}>
                        {attachments && setAttachments && (
                            <AddAttachmentButton
                                chatId={chat.id}
                                attachments={attachments}
                                setAttachments={setAttachments}
                            />
                        )}
                        <CopyTemplateButton
                            chat={chat}
                            setText={setText}
                            patientFilter={patientFilter}
                            appointment={appointment}
                            onCopyTemplatePatient={onCopyTemplatePatient}
                            compact={compact}
                        />
                        <AddLinkButton
                            chat={chat}
                            text={text}
                            setText={setText}
                            appointment={appointment}
                            compact={compact}
                        />
                        {chat.clinic?.tmp_feature_flags
                            ?.ai_suggestions_enabled && (
                            <SuggestChatResponseButton
                                chatId={chat.id}
                                text={text}
                                setText={setText}
                                compact={compact}
                            />
                        )}
                    </SharedStack>
                )}
                <Spacer />
                <SharedStack row fit flexShrink={0} ml="auto">
                    <SendButton
                        chat={chat}
                        isLoading={loading}
                        isDisabled={isSendDisabled}
                        handleSend={handleSendMessage}
                        isInternal={isInternal}
                        shouldCloseChatOnSend={shouldCloseChatOnSend}
                        setShouldCloseChatOnSend={setShouldCloseChatOnSend}
                        shouldOpenChatOnSend={shouldOpenChatOnSend}
                        setShouldOpenChatOnSend={setShouldOpenChatOnSend}
                        allowCtrlEnterToSend={allowCtrlEnterToSend}
                        setAllowCtrlEnterToSend={setAllowCtrlEnterToSend}
                        onScheduleSend={onScheduleSend}
                        isScheduling={isScheduling}
                        clientPhone={clientPhone}
                        messageMethods={messageMethods}
                    />
                </SharedStack>
            </Flex>
        </Flex>
    )
}
